import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Bonuses = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [bonuses, setBonuses] = useState([]);
  const [filteredBonuses, setFilteredBonuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('all');
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    employee_number: '',
    employee_name: '',
    amount: 0,
    notes: ''
  });

  const fetchBonuses = async () => {
    try {
      const response = await axios.get(`${API}/bonuses`, { headers: getAuthHeader() });
      setBonuses(response.data);
      setFilteredBonuses(response.data);
    } catch (error) {
      toast.error('Failed to fetch bonuses');
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`, { headers: getAuthHeader() });
      // Sort employees by employee_number in ascending order
      const sortedEmployees = response.data.sort((a, b) => {
        return parseInt(a.employee_number) - parseInt(b.employee_number);
      });
      setEmployees(sortedEmployees);
    } catch (error) {
      toast.error('Failed to fetch employees');
    }
  };

  useEffect(() => {
    fetchBonuses();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (selectedEmployeeFilter === 'all') {
      setFilteredBonuses(bonuses);
    } else {
      setFilteredBonuses(bonuses.filter(b => b.employee_number === selectedEmployeeFilter));
    }
  }, [selectedEmployeeFilter, bonuses]);

  const handleEmployeeSelect = (empNumber) => {
    const employee = employees.find(e => e.employee_number === empNumber);
    if (employee) {
      setFormData({
        ...formData,
        employee_number: empNumber,
        employee_name: employee.name
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user?.role !== 'admin') {
      toast.error('Only admin can add bonuses');
      return;
    }
    try {
      await axios.post(`${API}/bonuses`, formData, { headers: getAuthHeader() });
      toast.success('Bonus recorded successfully');
      setIsOpen(false);
      fetchBonuses();
      setFormData({
        employee_number: '',
        employee_name: '',
        amount: 0,
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record bonus');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('bonuses')}</h2>
          <select
            value={selectedEmployeeFilter}
            onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[200px]"
            data-testid="bonus-filter-select"
          >
            <option value="all">{t('allEmployees')}</option>
            {employees.map((emp) => (
              <option key={emp.employee_number} value={emp.employee_number}>
                {emp.employee_number} - {emp.name}
              </option>
            ))}
          </select>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-bonus-btn">
                <Plus className="w-4 h-4" />
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('recordBonus')}</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('recordBonus')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('employee')}</Label>
                  <select
                    value={formData.employee_number}
                    onChange={(e) => handleEmployeeSelect(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    required
                    data-testid="bonus-employee-select"
                  >
                    <option value="">{t('selectEmployee')}</option>
                    {employees.map((emp) => (
                      <option key={emp.employee_number} value={emp.employee_number}>
                        {emp.employee_number} - {emp.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')} ({t('currency')})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: parseFloat(e.target.value)})}
                    required
                    data-testid="bonus-amount-input"
                  />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    data-testid="bonus-notes-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="bonus-submit-btn">
                  <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('number')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredBonuses.map((bonus, idx) => (
              <tr key={idx} className="hover:bg-muted/30" data-testid={`bonus-row-${idx}`}>
                <td className="border border-border p-3">{formatDate(bonus.date)}</td>
                <td className="border border-border p-3 font-mono-numeric">{bonus.employee_number}</td>
                <td className="border border-border p-3">{bonus.employee_name}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-success">+{bonus.amount.toFixed(2)} {t('currency')}</td>
                <td className="border border-border p-3">{bonus.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredBonuses.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-bonuses">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noBonusesRecorded')}</p>
          </div>
        )}
      </div>

      {filteredBonuses.length > 0 && (
        <div className="mt-6 p-4 bg-muted/30 rounded-md">
          <div className="flex justify-between items-center">
            <span className="font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')} {t('bonuses')}:</span>
            <span className="text-2xl font-bold font-mono-numeric text-success">
              {filteredBonuses.reduce((sum, b) => sum + b.amount, 0).toFixed(2)} {t('currency')}
            </span>
          </div>
        </div>
      )}
    </Card>
  );
};